/**
 * User administration.
 *
 * The temporary password is shown exactly once, on creation, in a panel the
 * admin must dismiss deliberately — it is hashed immediately and cannot be
 * read back, so a toast that auto-dismisses would lose it.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  Copy,
  KeyRound,
  Link2,
  Loader2,
  Settings2,
  ShieldOff,
  Trash2,
  UserMinus,
  UserPlus,
  UserX,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../../api/client';
import DeleteRecordDialog from '../../components/DeleteRecordDialog';
import EmployeeDetailPanel from '../../components/EmployeeDetailPanel';
import { ROLE_LABELS, isCompanyAdmin, isOwner, useAuth } from '../../store/auth';
import type {
  Company,
  Employee,
  Office,
  Page,
  Role,
  ShiftPolicy,
  User,
} from '../../types';

/**
 * What each role may hand out in this form. Staff roles only.
 *
 * Employee is deliberately absent, and its absence is not a permission
 * change: ensure_roles_assignable on the server is unchanged and still allows
 * it, because onboarding needs it. An employee login is minted from a
 * placement — POST /onboarding creates the User and the Employee record
 * together, linked — and typing one in here produced a login with no employee
 * record behind it, which is the state half the payroll and attendance
 * screens have to special-case.
 *
 * So this is about which door the work goes through, not about who may open
 * it. Existing employees are untouched and still listed below.
 */
const ASSIGNABLE: Record<Role, Role[]> = {
  super_admin: ['super_admin', 'company_admin', 'manager', 'recruiter'],
  company_admin: ['manager', 'recruiter'],
  manager: ['recruiter'],
  recruiter: [],
  employee: [],
};

const USER_FORM_ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Owner (Super Admin)',
  company_admin: 'Admin',
  manager: 'Manager',
  recruiter: 'Recruiter',
  employee: 'Employee',
};


/**
 * Everyone reporting up to `id`, transitively.
 *
 * Mirrors the server's reporting_descendants so the manager picker never
 * offers a choice the API will refuse. The server is still the authority —
 * this only avoids walking somebody into a predictable 400.
 */
function descendantsOf(users: User[], id: string): Set<string> {
  const found = new Set<string>();
  let frontier = [id];
  while (frontier.length > 0) {
    const next = users
      .filter((u) => u.reportsToUserId && frontier.includes(u.reportsToUserId))
      .map((u) => u.id)
      .filter((candidate) => candidate !== id && !found.has(candidate));
    next.forEach((candidate) => found.add(candidate));
    frontier = next;
  }
  return found;
}

export default function UsersPage() {
  const me = useAuth((s) => s.user);
  // Erasure is company-admin and above, matching the API. Every other page
  // gates its admin-only controls this way; this one did not, so a manager
  // saw a destructive button that could only 403.
  const canErase = isCompanyAdmin(me);
  // Company Admin and Owner open the full record on its own page; a manager
  // keeps the compact slide-out, which never carried the sensitive fields the
  // page shows.
  const canViewFull = isCompanyAdmin(me);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  /**
   * Whether retired accounts are listed.
   *
   * Off by default. An anonymised employee and a deactivated one are both
   * is_active=false, and both have to stay: the employment record is kept for
   * statutory retention, and calls, placements and invoices point at the user
   * row. Neither is a reason to look at them every day — a list where most of
   * the names are people who have left is a list nobody reads, and the
   * repeated urge to delete them is what a cluttered list produces.
   */
  const [showRetired, setShowRetired] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [credential, setCredential] = useState<{ email: string; password: string } | null>(
    null,
  );
  const [resetLink, setResetLink] = useState<{ email: string; url: string; minutes: number } | null>(
    null,
  );
  const [offboarding, setOffboarding] = useState<{ user: User; employee: Employee } | null>(
    null,
  );
  const [erasing, setErasing] = useState<{ user: User; employee: Employee } | null>(null);
  /** Whose actions panel is open. One row at a time, by design. */
  const [managing, setManaging] = useState<User | null>(null);
  /** Whose profile the slide-out is showing. */
  const [viewing, setViewing] = useState<User | null>(null);

  /**
   * What this company can schedule people against.
   *
   * Both endpoints are company-scoped server-side, so an owner acting for one
   * company sees that company's options — and the form filters again by the
   * company chosen in the form itself, which may differ from the acting one.
   */
  const { data: policies } = useQuery<Page<ShiftPolicy>>({
    queryKey: ['shift-policies', 'assignable'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/shift-policies', { params: { limit: 200 } })).data,
  });

  const { data: offices } = useQuery<Page<Office>>({
    queryKey: ['offices', 'assignable'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/offices', { params: { limit: 200 } })).data,
  });

  const { data: companies } = useQuery<Page<Company>>({
    queryKey: ['admin-companies'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/companies', { params: { limit: 200 } })).data,
  });

  const { data: users, isLoading } = useQuery<Page<User>>({
    // In the key, or toggling it would serve the previous answer from cache.
    queryKey: ['admin-users', showRetired],
    queryFn: async () =>
      (
        await client.get('/api/v1/admin/users', {
          params: { limit: 200, includeInactive: showRetired },
        })
      ).data,
  });

  const { data: employees } = useQuery<Page<Employee>>({
    queryKey: ['admin-employees'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/employees', { params: { limit: 200 } })).data,
  });

  /** user id -> their employee record, for the offboarding actions. */
  const employeeByUser = new Map(
    (employees?.items ?? []).filter((e) => e.userId).map((e) => [e.userId as string, e]),
  );

  const assignable = me ? ASSIGNABLE[me.role] : [];

  const createUser = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await client.post('/api/v1/admin/users', body)).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setShowForm(false);
      if (data.temporaryPassword) {
        setCredential({ email: data.user.email, password: data.temporaryPassword });
      }
      toast.success(`${data.user.fullName} created`);
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not create the user')),
  });

  const setActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await client.patch(`/api/v1/admin/users/${id}`, { isActive })).data,
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(vars.isActive ? 'User reactivated' : 'User deactivated and signed out');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const setManager = useMutation({
    mutationFn: async ({ id, managerId }: { id: string; managerId: string | null }) =>
      (await client.patch(`/api/v1/admin/users/${id}`, { reportsToUserId: managerId })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Reporting line updated');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const offboard = useMutation({
    mutationFn: async ({
      employeeId,
      body,
    }: {
      employeeId: string;
      body: Record<string, unknown>;
    }) =>
      (await client.post(`/api/v1/admin/employees/${employeeId}/offboard`, body)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-employees'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setOffboarding(null);
      toast.success('Employee offboarded');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const anonymise = useMutation({
    mutationFn: async (employeeId: string) =>
      (await client.post(`/api/v1/admin/employees/${employeeId}/anonymise`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-employees'] });
      setErasing(null);
      toast.success('Personal data cleared');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const issueResetLink = useMutation({
    mutationFn: async (id: string) =>
      (await client.post(`/api/v1/admin/users/${id}/reset-link`)).data,
    onSuccess: (data) => {
      // Built here rather than server-side: the API has no idea what origin
      // the UI is served from, and guessing it wrong produces a link that
      // looks right and goes nowhere.
      setResetLink({
        email: data.email,
        url: `${window.location.origin}/reset-password?token=${encodeURIComponent(data.token)}`,
        minutes: data.validForMinutes,
      });
      toast.success('Reset link created');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const resetPassword = useMutation({
    mutationFn: async (id: string) =>
      (await client.post(`/api/v1/admin/users/${id}/reset-password`)).data,
    onSuccess: (data) => {
      const user = users?.items.find((u) => u.id === data.userId);
      setCredential({ email: user?.email ?? 'user', password: data.temporaryPassword });
      toast.success('Password reset; all their sessions were signed out');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="space-y-6">
      {resetLink && (
        <ResetLinkPanel
          email={resetLink.email}
          url={resetLink.url}
          minutes={resetLink.minutes}
          onDismiss={() => setResetLink(null)}
        />
      )}

      {credential && (
        <CredentialPanel
          email={credential.email}
          password={credential.password}
          onDismiss={() => setCredential(null)}
        />
      )}

      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-900">
          Users {users && <span className="badge bg-gray-100 text-gray-600">{users.total}</span>}
        </h2>
        <div className="flex items-center gap-3">
          {/* Says what it hides rather than "show inactive": the accounts
              behind it are people who have left, and the reason they are
              still here is worth carrying on the label. */}
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />
            Show retired accounts
          </label>
          {assignable.length > 0 && (
            <button type="button" className="btn-primary" onClick={() => setShowForm((v) => !v)}>
              <UserPlus className="h-4 w-4" />
              {showForm ? 'Cancel' : 'Add user'}
            </button>
          )}
        </div>
      </div>

      {showRetired && (
        <p className="text-xs text-gray-500">
          Retired accounts cannot be deleted and are not meant to be. The
          employment record is kept for statutory retention, and calls,
          placements and invoices reference the user row — removing it would
          take their history with it.
        </p>
      )}

      {showForm && (
        <UserForm
          policies={policies?.items ?? []}
          offices={offices?.items ?? []}
          companies={companies?.items ?? []}
          assignableRoles={assignable}
          defaultCompanyId={me?.companyId ?? undefined}
          managers={users?.items ?? []}
          submitting={createUser.isPending}
          onSubmit={(body) => createUser.mutate(body)}
        />
      )}

      {isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">Name</th>
                <th className="px-4 py-2.5 font-medium">Email</th>
                <th className="px-4 py-2.5 font-medium">Role</th>
                <th className="px-4 py-2.5 font-medium">Reports to</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users?.items.map((user) => (
                <tr key={user.id} className={user.isActive ? '' : 'bg-gray-50/60'}>
                  <td className="px-4 py-2.5 font-medium text-gray-900">
                    <button
                      type="button"
                      className="text-left hover:text-indigo-700 hover:underline"
                      onClick={() =>
                        canViewFull ? navigate(`/admin/users/${user.id}`) : setViewing(user)
                      }
                      title="Open Profile"
                    >
                      {user.fullName}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{user.email}</td>
                  <td className="px-4 py-2.5">
                    <span className="badge bg-indigo-50 text-indigo-700">
                      {ROLE_LABELS[user.role]}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {user.role === 'super_admin' ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : (
                      <select
                        className="input py-1 text-xs"
                        value={user.reportsToUserId ?? ''}
                        disabled={setManager.isPending}
                        onChange={(e) =>
                          setManager.mutate({
                            id: user.id,
                            managerId: e.target.value || null,
                          })
                        }
                      >
                        <option value="">No manager</option>
                        {(users?.items ?? [])
                          .filter(
                            (candidate) =>
                              candidate.id !== user.id
                              && candidate.isActive
                              && candidate.companyId === user.companyId
                              // Offering someone who reports to this user would
                              // be offering a loop the server refuses.
                              && !descendantsOf(users?.items ?? [], user.id).has(
                                candidate.id,
                              ),
                          )
                          .map((candidate) => (
                            <option key={candidate.id} value={candidate.id}>
                              {candidate.fullName}
                            </option>
                          ))}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {user.isActive ? (
                      <span className="badge bg-emerald-100 text-emerald-700">active</span>
                    ) : (
                      <span className="badge bg-gray-200 text-gray-600">disabled</span>
                    )}
                    {user.mustChangePassword && user.isActive && (
                      <span
                        className="badge ml-1 bg-amber-100 text-amber-700"
                        title="Has Not Set Their Own Password Yet"
                      >
                        temp pw
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {user.id === me?.id ? (
                      <span className="text-xs text-gray-400">you</span>
                    ) : (
                      <button
                        type="button"
                        className="btn-ghost px-2 py-1 text-xs"
                        onClick={() => setManaging(user)}
                      >
                        <Settings2 className="h-3.5 w-3.5" />
                        Manage
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Same dialog the office and candidate deletes use, so a refusal reads
          identically wherever it comes from — with the typed confirmation this
          one needs, because the account it removes exists nowhere else
          afterwards. */}
      {deletingUser && (
        <DeleteRecordDialog
          noun="account"
          checkUrl={`/api/v1/admin/users/${deletingUser.id}/deletable`}
          deleteUrl={`/api/v1/admin/users/${deletingUser.id}`}
          confirm={{
            phrase: deletingUser.email,
            field: 'confirmEmail',
            prompt: 'Type the account’s email address to confirm:',
          }}
          onClose={() => setDeletingUser(null)}
          onDeleted={() => {
            setDeletingUser(null);
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
          }}
        />
      )}

      {viewing && (
        <EmployeeDetailPanel
          key={viewing.id}
          userId={viewing.id}
          name={viewing.fullName}
          onClose={() => setViewing(null)}
        />
      )}

      {offboarding && (
        <OffboardDialog
          name={offboarding.user.fullName}
          submitting={offboard.isPending}
          onCancel={() => setOffboarding(null)}
          onConfirm={(body) =>
            offboard.mutate({ employeeId: offboarding.employee.id, body })
          }
        />
      )}

      {erasing && (
        <AnonymiseDialog
          name={erasing.user.fullName}
          submitting={anonymise.isPending}
          onCancel={() => setErasing(null)}
          onConfirm={() => anonymise.mutate(erasing.employee.id)}
        />
      )}

      {managing && (
        <ManageUserPanel
          user={managing}
          employee={employeeByUser.get(managing.id) ?? null}
          canErase={canErase}
          busy={
            issueResetLink.isPending || resetPassword.isPending || setActive.isPending
          }
          onClose={() => setManaging(null)}
          onResetLink={() => {
            issueResetLink.mutate(managing.id);
            setManaging(null);
          }}
          onResetPassword={() => {
            resetPassword.mutate(managing.id);
            setManaging(null);
          }}
          canDelete={isOwner(me)}
          onDelete={() => {
            // The manage panel closes: two stacked dialogs would leave the
            // one behind visible through the other, and the decision has
            // moved on to a different question.
            setDeletingUser(managing);
            setManaging(null);
          }}
          onToggleActive={() => {
            setActive.mutate({ id: managing.id, isActive: !managing.isActive });
            setManaging(null);
          }}
          onOffboard={(employee) => {
            setOffboarding({ user: managing, employee });
            setManaging(null);
          }}
          onErase={(employee) => {
            setErasing({ user: managing, employee });
            setManaging(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Everything that can be done to one account, named.
 *
 * Replaces a row of five unlabelled icons whose only explanation was a hover
 * title. Two things were wrong with that. Nobody can tell a shield from a
 * minus-person at 14 pixels, so the irreversible action sat in the same
 * undifferentiated row as "send a reset link", separated only by being red.
 * And the two employment actions were rendered *conditionally* — an admin
 * looking for "erase this person's data" found nothing at all, with no way to
 * learn that offboarding comes first.
 *
 * So the unavailable actions are shown with the reason in place of the
 * button, which is the pattern the invoice tracker already uses: a control
 * that is simply absent is a question nobody can answer from the screen.
 *
 * Ordered by how hard each is to undo — sign-in help, then access, then
 * employment, then erasure — and the last group is separated rather than
 * merely coloured.
 */
function ManageUserPanel({
  user,
  employee,
  canErase,
  busy,
  onClose,
  onResetLink,
  onResetPassword,
  onToggleActive,
  onDelete,
  canDelete,
  onOffboard,
  onErase,
}: {
  user: User;
  employee: Employee | null;
  canErase: boolean;
  canDelete: boolean;
  busy: boolean;
  onClose: () => void;
  onResetLink: () => void;
  onResetPassword: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
  onOffboard: (employee: Employee) => void;
  onErase: (employee: Employee) => void;
}) {
  const offboarded = employee?.status === 'offboarded';
  const erased = Boolean(employee?.anonymisedAt);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {user.fullName}
            </h3>
            <p className="truncate text-xs text-gray-500">{user.email}</p>
          </div>
          <button type="button" className="btn-ghost px-2 py-1" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <section>
            <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
              Signing in
            </h4>
            <Action
              icon={<Link2 className="h-4 w-4" />}
              label="Send a password link"
              hint="They choose their own password. Nothing changes until they use it."
              disabled={busy}
              onClick={onResetLink}
            />
            <Action
              icon={<KeyRound className="h-4 w-4" />}
              label="Issue a temporary password"
              hint="Shown once. They must change it at next sign-in."
              disabled={busy}
              onClick={onResetPassword}
            />
          </section>

          <section>
            <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
              Access
            </h4>
            <Action
              icon={
                user.isActive ? (
                  <UserX className="h-4 w-4" />
                ) : (
                  <Check className="h-4 w-4" />
                )
              }
              label={user.isActive ? 'Deactivate this login' : 'Reactivate this login'}
              hint={
                user.isActive
                  ? 'Signs them out everywhere. Their records are untouched, and this can be undone.'
                  : 'Restores access with their existing password.'
              }
              disabled={busy}
              onClick={onToggleActive}
            />
            {/* Beside Deactivate on purpose: it is the action this one gets
                mistaken for, and putting them together is what makes the
                difference between them readable. Owner only, matching the
                route — a company admin can already deactivate, which is the
                right answer for everything short of a mistake. */}
            {canDelete && (
              <Action
                icon={<Trash2 className="h-4 w-4" />}
                label="Delete this account"
                hint="Only for an account that never did anything — a typo'd address, a test login. Refused if anything references it, and it cannot be undone."
                disabled={busy}
                danger
                onClick={onDelete}
              />
            )}
          </section>

          <section>
            <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
              Employment
            </h4>
            {/* Acts on the employee record, not the login — so it says so
                where there is no employee record rather than vanishing. */}
            {!employee ? (
              <Unavailable text="No employee record is linked to this account, so there is nothing to offboard." />
            ) : offboarded ? (
              <Unavailable text="Already offboarded. Payroll and attendance history is kept." />
            ) : (
              <Action
                icon={<UserMinus className="h-4 w-4" />}
                label="Offboard"
                hint="Records an exit date and optionally disables the login. History is kept."
                onClick={() => onOffboard(employee)}
              />
            )}
          </section>

          <section className="border-t border-gray-200 pt-3">
            <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-red-700">
              Cannot Be Undone
            </h4>
            {/* The sequence is the thing that was impossible to discover.
                Erasing requires offboarding first, and the old UI expressed
                that by rendering nothing. */}
            {!canErase ? (
              <Unavailable text="Erasing personal data is restricted to admins and the owner." />
            ) : !employee ? (
              <Unavailable text="No employee record, so there is no personal data held here to erase." />
            ) : erased ? (
              <Unavailable text="Personal data has already been erased." />
            ) : !offboarded ? (
              <Unavailable text="Offboard this employee first — erasure only applies to somebody who has left." />
            ) : (
              <Action
                danger
                icon={<ShieldOff className="h-4 w-4" />}
                label="Erase personal data"
                hint="Scrubs their personal details for a DPDP erasure request. Permanent."
                onClick={() => onErase(employee)}
              />
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

/** One named action, with what it does underneath it. */
function Action({
  icon,
  label,
  hint,
  danger = false,
  disabled = false,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  hint: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left hover:bg-gray-50 disabled:opacity-50 ${
        danger ? 'text-red-700 hover:bg-red-50' : 'text-gray-900'
      }`}
      disabled={disabled}
      onClick={onClick}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        <span
          className={`block text-xs ${danger ? 'text-red-600' : 'text-gray-500'}`}
        >
          {hint}
        </span>
      </span>
    </button>
  );
}

/** Why an action is not on offer, in place of the action. */
function Unavailable({ text }: { text: string }) {
  return <p className="px-2 py-2 text-xs text-gray-500">{text}</p>;
}

/**
 * Offboarding: an exit date, and whether to take the login away with it.
 *
 * The employee record is kept — payroll history and attendance have to stay
 * reproducible — so this is a status change, not a deletion. The dialog says
 * so, because "offboard" reads like "remove" and here it is not.
 */
function OffboardDialog({
  name,
  submitting,
  onCancel,
  onConfirm,
}: {
  name: string;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (body: Record<string, unknown>) => void;
}) {
  const [dateOfExit, setDateOfExit] = useState(() => new Date().toISOString().slice(0, 10));
  const [deactivateLogin, setDeactivateLogin] = useState(true);

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4">
      <form
        className="card w-full max-w-md space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm({ dateOfExit, deactivateLogin });
        }}
      >
        <h3 className="text-sm font-semibold text-gray-900">Offboard {name}</h3>
        <p className="text-xs text-gray-500">
          Their employment record is kept — payroll history and attendance have
          to stay reproducible. This records that they have left.
        </p>

        <label className="block text-xs text-gray-600">
          Last Working Day
          <input
            className="input mt-1"
            type="date"
            value={dateOfExit}
            onChange={(e) => setDateOfExit(e.target.value)}
            required
          />
        </label>

        <label className="flex items-start gap-2 text-xs text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5 rounded border-gray-300"
            checked={deactivateLogin}
            onChange={(e) => setDeactivateLogin(e.target.checked)}
          />
          <span>
            Remove their access
            <span className="mt-0.5 block text-[11px] text-gray-500">
              Disables the login and signs out every session they have open.
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Offboard
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Erasure, kept visually and textually apart from offboarding.
 *
 * This is the DPDP request path and it cannot be undone: identifying fields are
 * cleared, the login is retired, and their documents are deleted from storage.
 * Typing the word is deliberate friction — an irreversible action one click
 * away from an ordinary one is a mistake waiting to happen.
 */
function AnonymiseDialog({
  name,
  submitting,
  onCancel,
  onConfirm,
}: {
  name: string;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const confirmed = typed.trim().toLowerCase() === 'erase';

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-md space-y-3 border-2 border-red-200">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            Erase {name}&apos;s personal data
          </h3>
        </div>

        <p className="text-xs text-gray-600">
          This cannot be undone. It clears their date of birth, personal email,
          address, emergency contact, PAN, Aadhaar, UAN, ESIC and bank details,
          retires their login, and deletes every document held for them.
        </p>
        <p className="text-xs text-gray-500">
          The employment record itself stays, so payroll filings that must be
          retained for statutory periods remain intact.
        </p>

        <label className="block text-xs text-gray-600">
          Type <span className="font-mono font-semibold">erase</span> to confirm
          <input
            spellCheck={false}
            className="input mt-1"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
          />
        </label>

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary bg-red-600 hover:bg-red-700"
            disabled={!confirmed || submitting}
            onClick={onConfirm}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Erase permanently
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * An issued reset link, shown once.
 *
 * Preferred over a temporary password: it works once, expires within the hour,
 * and the password that comes out of it is one only its owner ever knew. A
 * temporary password read out over a call exists in that call, and in whatever
 * was typed to relay it.
 */
function ResetLinkPanel({
  email,
  url,
  minutes,
  onDismiss,
}: {
  email: string;
  url: string;
  minutes: number;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="rounded-xl bg-indigo-50 p-4 ring-1 ring-indigo-200">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
        <Link2 className="h-4 w-4" />
        Reset link — shown once
      </h3>
      <p className="mt-1 text-sm text-indigo-800">
        Send this to <strong>{email}</strong> over a channel you trust. It works
        once, stops working in {minutes} minutes, and signs out every device
        already using the account.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="max-w-full flex-1 overflow-x-auto rounded-lg bg-white px-3 py-2 font-mono text-xs ring-1 ring-indigo-200">
          {url}
        </code>
        <button
          type="button"
          className="btn-secondary text-xs"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              toast.error('Could not copy — select the text manually');
            }
          }}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" className="btn-ghost text-xs" onClick={onDismiss}>
          Done
        </button>
      </div>
    </div>
  );
}

function CredentialPanel({
  email,
  password,
  onDismiss,
}: {
  email: string;
  password: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
        <AlertTriangle className="h-4 w-4" />
        Temporary password — shown once
      </h3>
      <p className="mt-1 text-sm text-amber-800">
        Give this to <strong>{email}</strong> over a channel you trust. It cannot be
        retrieved again; you would have to reset it. They must change it at first sign-in.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="rounded-lg bg-white px-3 py-2 font-mono text-sm ring-1 ring-amber-200">
          {password}
        </code>
        <button
          type="button"
          className="btn-secondary text-xs"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(password);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              toast.error('Could not copy — select the text manually');
            }
          }}
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" className="btn-ghost text-xs" onClick={onDismiss}>
          I've saved it
        </button>
      </div>
    </div>
  );
}

function UserForm({
  companies,
  assignableRoles,
  defaultCompanyId,
  managers,
  policies,
  offices,
  submitting,
  onSubmit,
}: {
  companies: Company[];
  assignableRoles: Role[];
  defaultCompanyId?: string;
  /** Existing active users who could be this person's manager. */
  managers: User[];
  policies: ShiftPolicy[];
  offices: Office[];
  submitting: boolean;
  onSubmit: (body: Record<string, unknown>) => void;
}) {
  const [form, setForm] = useState({
    email: '',
    fullName: '',
    // The least privileged the actor may assign, which is now recruiter
    // rather than employee. The fallback matches, so an actor with an empty
    // list cannot land the form on a role the dropdown does not offer.
    role: assignableRoles[assignableRoles.length - 1] ?? 'recruiter',
    companyId: defaultCompanyId ?? '',
    phone: '',
    designation: '',
    department: '',
    reportsToUserId: '',
    shiftPolicyId: '',
    officeId: '',
  });

  const isSuperAdmin = form.role === 'super_admin';

  /**
   * Only this company's, so an id from another tenant is never offered.
   *
   * The server refuses one anyway — a policy or office must belong to the
   * employee's own company — but a select that can produce a 422 is a select
   * that should not have contained the option.
   *
   * Nothing at all until a company is chosen. This used to read
   * `!form.companyId || ...`, so with no company picked it offered every
   * tenant's rows at once: three companies' policies, all named "Office Staff"
   * and "Field Recruiter", with no way to tell them apart — and mustPickPolicy
   * then *required* choosing one of them. Falling open is the wrong default
   * for a filter whose whole job is keeping tenants apart.
   */
  const forCompany = <T extends { companyId: string }>(rows: T[]) =>
    form.companyId ? rows.filter((r) => r.companyId === form.companyId) : [];

  /** No company chosen yet, so there is nothing meaningful to offer. */
  const awaitingCompany = !isSuperAdmin && !form.companyId;

  const availablePolicies = forCompany(policies);
  const availableOffices = forCompany(offices);

  /**
   * Required only when there is something to choose.
   *
   * A company with no shift policy yet cannot create its first user
   * otherwise, and every company starts in that state — including one in the
   * live data right now. Where nothing exists the form says what will happen
   * instead, so leaving it unset is a stated outcome rather than an oversight.
   */
  const mustPickPolicy = !isSuperAdmin && availablePolicies.length > 0;
  const mustPickOffice = !isSuperAdmin && availableOffices.length > 0;

  /**
   * One workplace means there is nothing to choose, so choose it.
   *
   * Most companies here have exactly one, and making somebody open a select
   * containing a single option is asking them to confirm a decision that has
   * already been made for them — while leaving it blank is the one way to fail
   * a required field on a form that could have filled it in.
   *
   * Only when there is exactly one. Two is a real choice and picking the first
   * would put somebody at the wrong site, which attendance then enforces with
   * a geofence.
   *
   * Only over a blank. A selection somebody made is never overwritten, so
   * switching company clears the stale office first (below) and this fills the
   * new one — rather than this quietly replacing a deliberate choice.
   */
  useEffect(() => {
    if (isSuperAdmin) return;
    if (availableOffices.length === 1 && !form.officeId) {
      setForm((current) => ({ ...current, officeId: availableOffices[0].id }));
    }
  }, [isSuperAdmin, availableOffices, form.officeId]);

  return (
    <form
      className="card grid gap-3 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          email: form.email.trim().toLowerCase(),
          fullName: form.fullName.trim(),
          role: form.role,
          // A super admin spans every company and must not carry one.
          companyId: isSuperAdmin ? null : form.companyId || null,
          phone: form.phone.trim() || null,
          designation: form.designation.trim() || null,
          department: form.department.trim() || null,
          // A super admin spans companies and sits outside any org chart.
          reportsToUserId: isSuperAdmin ? null : form.reportsToUserId || null,
          createEmployee: !isSuperAdmin,
          // Both hang off the employee record, so neither means anything
          // without one.
          shiftPolicyId: isSuperAdmin ? null : form.shiftPolicyId || null,
          officeId: isSuperAdmin ? null : form.officeId || null,
        });
      }}
    >
      <div>
        <label className="label" htmlFor="u-name">Full Name</label>
        <input
          spellCheck={false}
          id="u-name"
          className="input"
          value={form.fullName}
          onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          required
        />
      </div>
      <div>
        <label className="label" htmlFor="u-email">Email</label>
        <input
          spellCheck={false}
          id="u-email"
          type="email"
          className="input"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
        />
      </div>
      <div>
        <label className="label" htmlFor="u-phone">Phone</label>
        <input
          spellCheck={false}
          id="u-phone"
          className="input"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </div>
      <div>
        <label className="label" htmlFor="u-company">Company</label>
        <select
          id="u-company"
          className="input"
          value={isSuperAdmin ? '' : form.companyId}
          onChange={(e) =>
            // Changing company clears the two fields that belong to the old
            // one. A workplace and a shift policy are that company's rows, so
            // carrying them across would submit another tenant's id — the
            // server refuses it, but the form should not have offered it, and
            // the stale value also blocks the auto-select from filling in the
            // new company's only workplace.
            setForm({
              ...form,
              companyId: e.target.value,
              officeId: '',
              shiftPolicyId: '',
            })
          }
          disabled={isSuperAdmin}
          required={!isSuperAdmin}
        >
          <option value="">{isSuperAdmin ? 'All companies' : 'Select…'}</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
      {!isSuperAdmin && (
        <div>
          <label className="label" htmlFor="u-office">Workplace</label>
          <select
            id="u-office"
            className="input"
            value={form.officeId}
            onChange={(e) => setForm({ ...form, officeId: e.target.value })}
            required={mustPickOffice}
            disabled={awaitingCompany}
          >
            <option value="">
              {awaitingCompany
                ? 'Choose a company first'
                : availableOffices.length
                  ? 'Choose a workplace…'
                  : 'None available'}
            </option>
            {availableOffices.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          {awaitingCompany ? (
            <p className="mt-1 text-xs text-gray-500">
              Pick a company below and its workplaces will appear here.
            </p>
          ) : !availableOffices.length ? (
            <p className="mt-1 text-xs text-amber-700">
              No workplace exists for this company, so there is nothing to
              check their location against.
            </p>
          ) : null}
        </div>
      )}
      {!isSuperAdmin && (
        <div>
          {/*
            Labelled for what it decides, which is not hours.

            Every policy in the live data runs 08:30-17:30 Mon-Sat; the two
            names differ on geofence_required alone. Called "Shift policy",
            with options reading "Office Staff" and "Field Recruiter", the
            control gave no indication that picking one is what decides
            whether a new hire is geofenced to a building — and location
            tracking here has not been signed off legally, so that is not a
            thing to choose by accident or to have defaulted quietly.
          */}
          <label className="label" htmlFor="u-shift">Attendance Monitoring</label>
          <select
            id="u-shift"
            className="input"
            value={form.shiftPolicyId}
            onChange={(e) => setForm({ ...form, shiftPolicyId: e.target.value })}
            required={mustPickPolicy}
            disabled={awaitingCompany}
          >
            <option value="">
              {awaitingCompany
                ? 'Choose a company first'
                : availablePolicies.length
                  ? 'Choose how attendance is monitored…'
                  : 'None available'}
            </option>
            {/*
              The name kept and the consequence added beside it. The names are
              the company's own and are what the Workplaces screen shows, so
              replacing them would leave two screens naming one row
              differently; but "Office Staff" on its own does not tell anybody
              what selecting it does to the person being created.
            */}
            {availablePolicies.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {' — '}
                {p.geofenceRequired
                  ? 'geofenced to the workplace'
                  : 'location recorded, not enforced'}
              </option>
            ))}
          </select>
          {/* Three states, not two. "This company has no shift policy" is a
              statement about a company, and saying it before one is chosen
              would be asserting something nobody has told the form yet. */}
          {awaitingCompany ? (
            <p className="mt-1 text-xs text-gray-500">
              Pick a company below and its monitoring options will appear here.
            </p>
          ) : !availablePolicies.length ? (
            /* The fallback is real and invisible, so it is spelled out where
               somebody is about to trigger it. */
            <p className="mt-1 text-xs text-amber-700">
              This company has no monitoring policy yet, so this person will be
              scheduled 08:30–17:30 against hardcoded times with nothing
              recorded about which. Create one under Workplaces.
            </p>
          ) : (
            /* The hours are the same either way, and saying so is the point:
               otherwise "Attendance monitoring" reads like it might change
               when somebody works. */
            <p className="mt-1 text-xs text-gray-500">
              08:30–17:30 either way. This chooses whether presence is checked
              against the workplace boundary, not the working hours.
            </p>
          )}
        </div>
      )}
      {!isSuperAdmin && (
        <div>
          <label className="label" htmlFor="u-manager">Reports To</label>
          <select
            id="u-manager"
            className="input"
            value={form.reportsToUserId}
            onChange={(e) => setForm({ ...form, reportsToUserId: e.target.value })}
          >
            <option value="">No manager</option>
            {managers
              // Same rule as forCompany, and the same reason: with no company
              // chosen this listed every tenant's managers at once. A person's
              // manager has to be someone in their own company.
              .filter(
                (m) =>
                  m.isActive
                  && !!form.companyId
                  && m.companyId === form.companyId,
              )
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullName}
                </option>
              ))}
          </select>
        </div>
      )}
      <div>
        <label className="label" htmlFor="u-role">Role</label>
        <select
          id="u-role"
          className="input"
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
        >
          {assignableRoles
            .filter((r) => r in USER_FORM_ROLE_LABELS)
            .map((r) => (
              <option key={r} value={r}>{USER_FORM_ROLE_LABELS[r]}</option>
            ))}
        </select>
      </div>
      <div>
        <label className="label" htmlFor="u-desig">Designation</label>
        <input
          spellCheck={false}
          id="u-desig"
          className="input"
          value={form.designation}
          onChange={(e) => setForm({ ...form, designation: e.target.value })}
        />
      </div>

      <div className="sm:col-span-2">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
          Create user
        </button>
        <p className="mt-2 text-xs text-gray-500">
          A temporary password is generated and shown once. There is no public signup.
        </p>
      </div>
    </form>
  );
}
